import React from "react";
import { FiCheck } from "react-icons/fi";
import SEO from "../components/SEO";
import Layout from "../components/Layout";
import Stats from "../components/Stats";
import CTA from "../components/CTA";
import site from "../data/site";

const About = () => (
  <Layout>
    <SEO title="About" description="Atlas Brokerage — a senior-led technology practice covering software, networks, cloud and managed services." />

    <section className="service-hero">
      <div className="container">
        <div className="crumbs">
          <a href="/">Home</a><span className="sep">/</span> About
        </div>
        <span className="eyebrow eyebrow--light">About {site.brand}</span>
        <h1>One technology partner. The entire stack. Senior people on every engagement.</h1>
        <p className="lead">
          We exist to be the technology team that mid-market and enterprise customers wish they could hire internally —
          and to stay long enough to make ourselves replaceable, by choice rather than by accident.
        </p>
      </div>
    </section>

    <Stats />

    <section className="section">
      <div className="container">
        <div className="split">
          <div>
            <span className="eyebrow">Our story</span>
            <h2>Built by engineers who got tired of explaining the same things.</h2>
            <p>
              Atlas Brokerage was founded by engineers and delivery leaders who spent years watching technology
              programmes fail in the same predictable ways: undefined scope, vendor lock-in, junior teams shipping
              senior decisions, and reporting designed to obscure rather than inform.
            </p>
            <p>
              We built the firm we wished we could have hired in those situations. Senior practitioners across every
              discipline, an explicit refusal to take referral fees from software vendors, and contracts written
              around outcomes instead of activity.
            </p>
            <p>
              Today, headquartered in Sofia, Bulgaria, we serve customers across the European Union, the
              Middle East and Africa — delivering work that ranges from a single CRM rollout to running the
              complete IT function of a regional business.
            </p>
          </div>
          <div className="split-media">
            <span className="glyph">∞</span>
          </div>
        </div>
      </div>
    </section>

    <section className="section section--cream">
      <div className="container">
        <div className="section-title">
          <span className="eyebrow" style={{ display: "inline-flex", margin: "0 auto 1rem" }}>What makes us different</span>
          <h2>Four commitments we put in writing.</h2>
        </div>

        <div className="grid grid--2">
          {[
            {
              t: "Senior-only delivery",
              d: "Every engagement is owned by an engineer or PM with at least ten years in the field. We do not staff training with you."
            },
            {
              t: "Vendor-independent advice",
              d: "We accept no referral commissions from software, hardware or hosting vendors. The recommendation you get is the recommendation we believe in."
            },
            {
              t: "Documented handover",
              d: "Everything we build is documented to the level that a competent successor team could take over without a phone call to us."
            },
            {
              t: "Measurable SLAs",
              d: "Response time, resolution time, uptime, customer satisfaction — written into every managed-service contract, reported every month."
            }
          ].map((c) => (
            <div key={c.t} style={{ background: "#fff", padding: "1.75rem", borderRadius: 18, border: "1px solid #d4dae3" }}>
              <div style={{ display: "flex", alignItems: "center", gap: ".75rem", marginBottom: ".5rem" }}>
                <span style={{ width: 28, height: 28, borderRadius: "50%", background: "#f4ebd6", color: "#c9a86a", display: "grid", placeItems: "center" }}>
                  <FiCheck />
                </span>
                <h3 style={{ margin: 0, fontSize: "1.2rem" }}>{c.t}</h3>
              </div>
              <p style={{ margin: 0, color: "#5b6a7f" }}>{c.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="section">
      <div className="container">
        <div className="section-title section-title--left">
          <span className="eyebrow">Industries we know</span>
          <h2>Context, not just credentials.</h2>
          <p>Our engineers have built and operated systems inside each of the sectors below. We bring that context to the first conversation.</p>
        </div>
        <div className="industries">
          {site.industries.map((i) => <span key={i} className="pill">{i}</span>)}
        </div>
      </div>
    </section>

    <CTA
      eyebrow="Work with us"
      title="A short conversation is enough to know if we are the right fit."
      text="Send us a brief on what you are trying to change. We will respond within one business day with a clear, honest next step."
    />
  </Layout>
);

export default About;
